import React from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, MapPin, Mail, Shield, Store, Sparkles, ChevronRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { fmt } from "../lib/format";

const roleMeta = {
  admin: { icon: Shield, label: "Admin", tone: "text-amber-300" },
  commerce: { icon: Store, label: "Commerçant", tone: "text-sky-300" },
  creator: { icon: Sparkles, label: "Créateur", tone: "text-fuchsia-300" },
};

export default function ProfilePage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const meta = roleMeta[user.role] || roleMeta.creator;

  return (
    <div className="space-y-5 animate-fade-in">
      <h1 className="font-heading text-2xl font-extrabold">{t("myProfile")}</h1>

      <div className="relative overflow-hidden rounded-3xl border border-cardline bg-card2/60 p-6 text-center">
        {user.banner && <img src={user.banner} alt="" className="absolute inset-0 h-24 w-full object-cover opacity-30" />}
        <div className="relative">
          <img src={user.avatar} alt={user.name} className="mx-auto h-24 w-24 rounded-full border-4 border-ink object-cover" data-testid="my-avatar" />
          <div className="mt-2 flex items-center justify-center gap-1.5">
            <h2 className="font-heading text-xl font-extrabold">{user.name}</h2>
            {user.verified && <BadgeCheck size={16} className="text-gold" />}
          </div>
          <div className={`mt-1 inline-flex items-center gap-1.5 rounded-full bg-ink2/70 px-3 py-1 text-xs font-semibold ${meta.tone}`}>
            <meta.icon size={13} /> {meta.label}
          </div>
          {user.location && <div className="mt-2 flex items-center justify-center gap-1 text-sm text-slate-400"><MapPin size={13} /> {user.location}</div>}
          <div className="mt-1 flex items-center justify-center gap-1 text-sm text-slate-400"><Mail size={13} /> {user.email}</div>
        </div>
      </div>

      {user.role === "creator" && (
        <>
          <div className="grid grid-cols-4 gap-2">
            {[[fmt(user.followers), t("followers")], [fmt(user.avg_views), t("avgViews")], [`${user.engagement_rate}%`, t("engagement")], [user.posts, t("posts")]].map(([v, l], i) => (
              <div key={i} className="rounded-2xl border border-cardline bg-card2/60 p-3 text-center">
                <div className="font-heading text-lg font-extrabold text-gold">{v}</div>
                <div className="text-[11px] text-slate-400">{l}</div>
              </div>
            ))}
          </div>
          <button onClick={() => navigate(`/creators/${user.id}`)} data-testid="view-public-profile-btn"
            className="flex w-full items-center justify-between rounded-2xl border border-cardline bg-card2/60 px-4 py-3.5 text-sm font-semibold">
            {t("viewProfile")} <ChevronRight size={18} className="text-gold" />
          </button>
        </>
      )}

      <button onClick={() => navigate("/campaigns")} data-testid="profile-campaigns-btn"
        className="flex w-full items-center justify-between rounded-2xl border border-cardline bg-card2/60 px-4 py-3.5 text-sm font-semibold">
        {t("campaigns")} <ChevronRight size={18} className="text-gold" />
      </button>
    </div>
  );
}
